import React, { useState, useEffect } from 'react';
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Info } from '@mui/icons-material';
import { format } from 'date-fns';
import supabase from '../../supabaseClient';
import { SHIFT_CONFIGS } from '../../constants/shiftData';


// Helper function to calculate percentage with proper rounding
const calculatePercentage = (actual, expected) => {
  if (expected === 0) return 0;
  return Math.round((actual / expected) * 100 * 100) / 100; // Round to 2 decimal places
};

// Helper function to get color based on percentage
const getPercentageColor = (percentage) => {
  if (percentage >= 80) return 'success';
  if (percentage >= 50) return 'warning';
  if (percentage >= 25) return 'error';
  return 'error';
};

// Helper function to generate expected dates for a class based on its shift
const generateExpectedDates = (classItem, startDate, endDate) => {
  const shiftConfig = SHIFT_CONFIGS[classItem.shift];
  if (!shiftConfig || !classItem.shift) {
    console.warn(`Unknown shift type: ${classItem.shift} for class ${classItem.name || classItem.id}`);
    return [];
  }

  const expectedDates = [];
  const current = new Date(startDate);
  const end = new Date(endDate);

  while (current <= end) {
    const dayOfWeek = current.getDay(); // 0=Sunday, 1=Monday, etc.
    if (shiftConfig.workingDays.includes(dayOfWeek)) {
      expectedDates.push({
        date: current.toISOString().split('T')[0],
        classId: classItem.id,
        className: classItem.name || `Class ${classItem.id}`,
        shift: classItem.shift
      });
    }
    current.setDate(current.getDate() + 1);
  }

  return expectedDates;
};


function TeacherActivityTracker() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [inactivityThreshold, setInactivityThreshold] = useState(30); // days

  useEffect(() => {
    fetchTeacherActivity();
  }, [inactivityThreshold]); // eslint-disable-line react-hooks/exhaustive-deps


  const fetchTeacherActivity = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch teachers with their last_active timestamp and recent activity data (only active teachers)
      const { data: teachersData, error: teachersError } = await supabase
        .from('teachers')
        .select(`
          id,
          first_name,
          last_name,
          email,
          last_active,
          created_at
        `)
        .eq('is_active', true)
        .order('last_active', { ascending: false, nullsFirst: false });

      if (teachersError) throw teachersError;

      // For each teacher, get their recent activity counts
      const teachersWithActivity = await Promise.all(
        teachersData.map(async (teacher) => {
          const cutoffDate = new Date();
          cutoffDate.setDate(cutoffDate.getDate() - inactivityThreshold);
          const cutoffDateString = cutoffDate.toISOString().split('T')[0];

          // Get classes taught by this teacher with shift information
          const { data: classes } = await supabase
            .from('classes')
            .select('id, shift, name')
            .eq('teacher_id', teacher.id);

          const classIds = classes?.map(c => c.id) || [];

          // Get substitute assignments for this teacher
          const { data: substituteAssignments } = await supabase
            .from('substitute_assignments')
            .select(`
              id,
              start_date,
              end_date,
              status,
              classes:class_id (id, shift, name)
            `)
            .eq('substitute_teacher_id', teacher.id)
            .in('status', ['pending', 'completed'])
            .lte('start_date', cutoffDateString)
            .gte('end_date', cutoffDateString);

          const substituteClasses = substituteAssignments || [];

          // Early return for teachers with no regular classes AND no substitute assignments
          if ((!classes || classes.length === 0) && substituteClasses.length === 0) {
            return {
              ...teacher,
              totalExpectedShifts: 0,
              attendanceShiftsRecorded: 0,
              quranShiftsRecorded: 0,
              greenbookShiftsRecorded: 0,
              attendancePercentage: 0,
              quranProgressPercentage: 0,
              greenbookProgressPercentage: 0,
              hasAnyRecentActivity: false,
              substituteShifts: 0,
            };
          }

          // Check for unrecognized shift types
          const unrecognizedShifts = classes.filter(c => !SHIFT_CONFIGS[c.shift]);
          if (unrecognizedShifts.length > 0) {
            console.warn(`Teacher ${teacher.first_name} ${teacher.last_name} has classes with unrecognized shifts:`, unrecognizedShifts.map(c => `${c.name}: "${c.shift}"`));
          }
          

          // We'll calculate totalExpectedShifts from the actual expected dates to ensure consistency
          let totalExpectedShifts = 0;

          let attendanceShiftsRecorded = 0;
          let quranShiftsRecorded = 0;
          let greenbookShiftsRecorded = 0;

          if (classIds.length > 0 || substituteClasses.length > 0) {
            // Generate all expected dates for regular classes
            const today = new Date().toISOString().split('T')[0];
            const endDateString = today; // Always use today as end date
            
            const allExpectedDates = [];
            
            // Add regular class dates
            if (classes && classes.length > 0) {
              classes.forEach(classItem => {
                const expectedDates = generateExpectedDates(classItem, cutoffDateString, endDateString);
                allExpectedDates.push(...expectedDates);
              });
            }
            
            // Add substitute teaching dates
            substituteClasses.forEach(subAssignment => {
              if (subAssignment.classes && SHIFT_CONFIGS[subAssignment.classes.shift]) {
                // Generate expected dates for the substitute assignment period
                const subStartDate = new Date(Math.max(new Date(subAssignment.start_date), new Date(cutoffDateString)));
                const subEndDate = new Date(Math.min(new Date(subAssignment.end_date), new Date(endDateString)));
                
                if (subStartDate <= subEndDate) {
                  const subExpectedDates = generateExpectedDates(
                    subAssignment.classes, 
                    subStartDate.toISOString().split('T')[0], 
                    subEndDate.toISOString().split('T')[0]
                  );
                  allExpectedDates.push(...subExpectedDates.map(date => ({
                    ...date,
                    isSubstitute: true,
                    assignmentId: subAssignment.id
                  })));
                }
              }
            });
            
            // Set totalExpectedShifts based on actual generated dates
            totalExpectedShifts = allExpectedDates.length;

            // Get all relevant class IDs (regular classes + substitute classes)
            const allClassIds = [
              ...classIds,
              ...substituteClasses.map(sub => sub.classes.id)
            ];

            // Get unique class_id + date combinations for attendance
            const { data: attendanceData, error: attendanceError } = await supabase
              .from('attendance')
              .select('class_id, date, created_by')
              .in('class_id', allClassIds)
              .gte('date', cutoffDateString)
              .lte('date', endDateString);

            if (attendanceError) {
              console.error(`Error fetching attendance for teacher ${teacher.first_name} ${teacher.last_name}:`, attendanceError);
            }

            // Get unique class_id + date combinations for Quran progress
            const { data: quranData } = await supabase
              .from('student_quran_progress')
              .select('class_id, date, created_by')
              .in('class_id', allClassIds)
              .gte('date', cutoffDateString)
              .lte('date', endDateString);

            // Get unique class_id + date combinations for Greenbook progress
            const { data: greenbookData } = await supabase
              .from('student_greenbook_progress')
              .select('class_id, date, created_by')
              .in('class_id', allClassIds)
              .gte('date', cutoffDateString)
              .lte('date', endDateString);

            // Filter records to only count those where this teacher was responsible
            const filterRecordsForTeacher = (records) => {
              return records.filter(record => {
                const recordDate = record.date;
                const classId = record.class_id;
                
                // Check if this was during a substitute assignment for this teacher
                const wasSubstitute = substituteClasses.some(sub => 
                  sub.classes.id === classId &&
                  recordDate >= sub.start_date &&
                  recordDate <= sub.end_date
                );
                
                // If it was during substitute period, only count if this teacher created it
                if (wasSubstitute) {
                  return record.created_by === teacher.id;
                }
                
                // If it's a regular class, count all records for that class
                return classIds.includes(classId);
              });
            };

            const relevantAttendance = filterRecordsForTeacher(attendanceData || []);
            const relevantQuran = filterRecordsForTeacher(quranData || []);
            const relevantGreenbook = filterRecordsForTeacher(greenbookData || []);

            // Create sets of recorded class-date combinations
            const recordedAttendance = new Set(
              relevantAttendance.map(record => `${record.class_id}-${record.date}`)
            );
            const recordedQuran = new Set(
              relevantQuran.map(record => `${record.class_id}-${record.date}`)
            );
            const recordedGreenbook = new Set(
              relevantGreenbook.map(record => `${record.class_id}-${record.date}`)
            );

            // Count recorded shifts
            attendanceShiftsRecorded = recordedAttendance.size;
            quranShiftsRecorded = recordedQuran.size;
            greenbookShiftsRecorded = recordedGreenbook.size;
          } else {
            // If no classes, ensure totalExpectedShifts is 0
            totalExpectedShifts = 0;
          }

          // Calculate percentages
          const attendancePercentage = calculatePercentage(attendanceShiftsRecorded, totalExpectedShifts);
          const quranProgressPercentage = calculatePercentage(quranShiftsRecorded, totalExpectedShifts);
          const greenbookProgressPercentage = calculatePercentage(greenbookShiftsRecorded, totalExpectedShifts);



          return {
            ...teacher,
            totalExpectedShifts,
            attendanceShiftsRecorded,
            quranShiftsRecorded,
            greenbookShiftsRecorded,
            attendancePercentage,
            quranProgressPercentage,
            greenbookProgressPercentage,
            hasAnyRecentActivity: attendanceShiftsRecorded > 0 || quranShiftsRecorded > 0 || greenbookShiftsRecorded > 0,
            substituteShifts: substituteClasses.length,
            hasSubstituteActivity: substituteClasses.length > 0,
          };
        })
      );

      setTeachers(teachersWithActivity);
    } catch (error) {
      console.error('Error fetching teacher activity:', error);
      setError('Failed to load teacher activity data');
    } finally {
      setLoading(false);
    }
  };

  const getActivityStatus = (teacher) => {
    const { last_active, totalExpectedShifts } = teacher;
    
    if (!last_active) {
      return { status: 'Never Active', color: 'error', severity: 'high' };
    }

    if (totalExpectedShifts === 0) {
      return { status: 'No Classes', color: 'default', severity: 'low' };
    }

    const lastActiveDate = new Date(last_active);
    const daysSinceActive = Math.floor((new Date() - lastActiveDate) / (1000 * 60 * 60 * 24));

    // Calculate overall activity percentage (average of all three activities)
    const avgActivityPercentage = (teacher.attendancePercentage + teacher.quranProgressPercentage + teacher.greenbookProgressPercentage) / 3;

    if (daysSinceActive > inactivityThreshold && avgActivityPercentage < 25) {
      return { status: 'Inactive', color: 'error', severity: 'high' };
    } else if (daysSinceActive > inactivityThreshold || avgActivityPercentage < 50) {
      return { status: 'Low Activity', color: 'warning', severity: 'medium' };
    } else {
      return { status: 'Active', color: 'success', severity: 'low' };
    }
  };

  const formatLastActive = (lastActive) => {
    if (!lastActive) return 'Never';
    
    const date = new Date(lastActive);
    const now = new Date();
    const diffInDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffInDays === 0) return 'Today';
    if (diffInDays === 1) return 'Yesterday';
    if (diffInDays < 7) return `${diffInDays} days ago`;
    
    return format(date, 'MMM d, yyyy');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  // Sort teachers by activity status (inactive first)
  const sortedTeachers = [...teachers].sort((a, b) => {
    const statusA = getActivityStatus(a);
    const statusB = getActivityStatus(b);
    
    // Sort by severity (high -> medium -> low)
    const severityOrder = { high: 0, medium: 1, low: 2 };
    const severityDiff = severityOrder[statusA.severity] - severityOrder[statusB.severity];
    
    if (severityDiff !== 0) return severityDiff;
    
    // If same severity, sort by last_active (oldest first for inactive)
    if (!a.last_active && !b.last_active) return 0;
    if (!a.last_active) return -1;
    if (!b.last_active) return 1;
    
    return new Date(a.last_active) - new Date(b.last_active);
  });

  return (
    <Box>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' }, 
        mb: { xs: 1.5, sm: 2 },
        gap: { xs: 1.5, sm: 0 },
      }}>
        <Typography 
          variant="h6"
          sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
        >
          Teacher Activity Tracking
        </Typography>
        <FormControl 
          size="small" 
          sx={{ 
            minWidth: { xs: '100%', sm: 160 },
            width: { xs: '100%', sm: 'auto' },
          }}
        >
          <InputLabel>Time Period</InputLabel>
          <Select
            value={inactivityThreshold}
            label="Time Period"
            onChange={(e) => setInactivityThreshold(e.target.value)}
          >
            <MenuItem value={7}>1 week</MenuItem>
            <MenuItem value={14}>2 weeks</MenuItem>
            <MenuItem value={30}>1 month</MenuItem>
            <MenuItem value={180}>6 months</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <TableContainer 
        component={Paper} 
        sx={{ 
          maxHeight: { xs: 'calc(100vh - 300px)', sm: 500 },
          overflow: 'auto',
          '&::-webkit-scrollbar': {
            height: '8px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
            borderRadius: '4px',
          },
        }}
      >
        <Table stickyHeader sx={{ minWidth: { xs: 800, sm: 'auto' } }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ 
                fontWeight: 600,
                fontSize: { xs: '0.875rem', sm: '1rem' },
                whiteSpace: 'nowrap',
              }}>
                Teacher
              </TableCell>
              <TableCell sx={{ 
                fontWeight: 600,
                fontSize: { xs: '0.875rem', sm: '1rem' },
                whiteSpace: 'nowrap',
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  Status
                  <Tooltip
                    title={
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                          Status Definitions:
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          <strong>Active:</strong> Teacher has been active and recorded data within the selected time period
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          <strong>Low Activity:</strong> Teacher has been active OR recorded data, but not both within the selected time period
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          <strong>Inactive:</strong> Teacher has not been active AND has not recorded any data within the selected time period
                        </Typography>
                        <Typography variant="body2">
                          <strong>Never Active:</strong> Teacher has never logged into the system
                        </Typography>
                      </Box>
                    }
                    arrow
                    placement="top"
                  >
                    <Info sx={{ fontSize: { xs: 14, sm: 16 }, color: 'primary.main', cursor: 'pointer' }} />
                  </Tooltip>
                </Box>
              </TableCell>
              <TableCell sx={{ 
                fontWeight: 600,
                fontSize: { xs: '0.875rem', sm: '1rem' },
                whiteSpace: 'nowrap',
                display: { xs: 'none', md: 'table-cell' },
              }}>
                Last Active
              </TableCell>
              <TableCell align="center" sx={{ 
                fontWeight: 600,
                fontSize: { xs: '0.875rem', sm: '1rem' },
                whiteSpace: 'nowrap',
              }}>
                Attendance %
              </TableCell>
              <TableCell align="center" sx={{ 
                fontWeight: 600,
                fontSize: { xs: '0.875rem', sm: '1rem' },
                whiteSpace: 'nowrap',
                display: { xs: 'none', lg: 'table-cell' },
              }}>
                Quran Progress %
              </TableCell>
              <TableCell align="center" sx={{ 
                fontWeight: 600,
                fontSize: { xs: '0.875rem', sm: '1rem' },
                whiteSpace: 'nowrap',
                display: { xs: 'none', lg: 'table-cell' },
              }}>
                Greenbook Progress %
              </TableCell>
              <TableCell align="center" sx={{ 
                fontWeight: 600,
                fontSize: { xs: '0.875rem', sm: '1rem' },
                whiteSpace: 'nowrap',
                display: { xs: 'none', md: 'table-cell' },
              }}>
                <Tooltip title="Number of substitute assignments during the selected period">
                  <span>Substitute Shifts</span>
                </Tooltip>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedTeachers.map((teacher) => {
              const activityStatus = getActivityStatus(teacher);
              return (
                <TableRow key={teacher.id}>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {teacher.first_name} {teacher.last_name}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {teacher.email}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {activityStatus.status === 'Low Activity' ? (
                      <Tooltip
                        title={
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                              Low Activity Breakdown:
                            </Typography>
                            {teacher.last_active && 
                              Math.floor((new Date() - new Date(teacher.last_active)) / (1000 * 60 * 60 * 24)) <= inactivityThreshold ? (
                              <Typography variant="body2" sx={{ mb: 0.5 }}>
                                ✓ Has been active recently
                              </Typography>
                            ) : (
                              <Typography variant="body2" sx={{ mb: 0.5 }}>
                                ✗ Has not been active recently
                              </Typography>
                            )}
                            <Typography variant="body2" sx={{ mb: 0.5 }}>
                              Attendance: {teacher.attendancePercentage}% ({teacher.attendanceShiftsRecorded}/{teacher.totalExpectedShifts} shifts)
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 0.5 }}>
                              Quran Progress: {teacher.quranProgressPercentage}% ({teacher.quranShiftsRecorded}/{teacher.totalExpectedShifts} shifts)
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              Greenbook Progress: {teacher.greenbookProgressPercentage}% ({teacher.greenbookShiftsRecorded}/{teacher.totalExpectedShifts} shifts)
                            </Typography>
                          </Box>
                        }
                        arrow
                        placement="right"
                      >
                        <Chip
                          label={activityStatus.status}
                          color={activityStatus.color}
                          size="small"
                          variant="filled"
                          sx={{ 
                            cursor: 'pointer',
                            fontSize: { xs: '0.65rem', sm: '0.75rem' },
                            height: { xs: '20px', sm: '24px' },
                          }}
                        />
                      </Tooltip>
                    ) : activityStatus.status === 'Active' ? (
                      <Tooltip
                        title={
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                              Active Status Breakdown:
                            </Typography>
                            {teacher.last_active && 
                              Math.floor((new Date() - new Date(teacher.last_active)) / (1000 * 60 * 60 * 24)) <= inactivityThreshold ? (
                              <Typography variant="body2" sx={{ mb: 0.5 }}>
                                ✓ Has been active recently
                              </Typography>
                            ) : (
                              <Typography variant="body2" sx={{ mb: 0.5 }}>
                                ✗ Has not been active recently
                              </Typography>
                            )}
                            <Typography variant="body2" sx={{ mb: 0.5 }}>
                              Attendance: {teacher.attendancePercentage}% ({teacher.attendanceShiftsRecorded}/{teacher.totalExpectedShifts} shifts)
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 0.5 }}>
                              Quran Progress: {teacher.quranProgressPercentage}% ({teacher.quranShiftsRecorded}/{teacher.totalExpectedShifts} shifts)
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              Greenbook Progress: {teacher.greenbookProgressPercentage}% ({teacher.greenbookShiftsRecorded}/{teacher.totalExpectedShifts} shifts)
                            </Typography>
                          </Box>
                        }
                        arrow
                        placement="right"
                      >
                        <Chip
                          label={activityStatus.status}
                          color={activityStatus.color}
                          size="small"
                          variant="filled"
                          sx={{ 
                            cursor: 'pointer',
                            fontSize: { xs: '0.65rem', sm: '0.75rem' },
                            height: { xs: '20px', sm: '24px' },
                          }}
                        />
                      </Tooltip>
                    ) : (
                      <Chip
                        label={activityStatus.status}
                        color={activityStatus.color}
                        size="small"
                        variant="filled"
                        sx={{ 
                          fontSize: { xs: '0.65rem', sm: '0.75rem' },
                          height: { xs: '20px', sm: '24px' },
                        }}
                      />
                    )}
                  </TableCell>
                  <TableCell sx={{ 
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    display: { xs: 'none', md: 'table-cell' },
                  }}>
                    <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                      {formatLastActive(teacher.last_active)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    {teacher.totalExpectedShifts > 0 ? (
                      <Tooltip
                        title={`Attendance: ${teacher.attendanceShiftsRecorded} out of ${teacher.totalExpectedShifts} expected shifts`}
                        arrow
                        placement="top"
                      >
                        <Chip
                          label={`${teacher.attendancePercentage}%`}
                          color={getPercentageColor(teacher.attendancePercentage)}
                          size="small"
                          variant="filled"
                          sx={{ 
                            fontSize: { xs: '0.65rem', sm: '0.75rem' },
                            height: { xs: '20px', sm: '24px' },
                          }}
                        />
                      </Tooltip>
                    ) : (
                      <Chip 
                        label="N/A" 
                        color="default" 
                        size="small" 
                        variant="outlined"
                        sx={{ 
                          fontSize: { xs: '0.65rem', sm: '0.75rem' },
                          height: { xs: '20px', sm: '24px' },
                        }}
                      />
                    )}
                    {/* Show Quran and Greenbook percentages on mobile */}
                    {isMobile && teacher.totalExpectedShifts > 0 && (
                      <Box sx={{ mt: 0.5, display: 'flex', gap: 0.5, justifyContent: 'center', flexWrap: 'wrap' }}>
                        <Tooltip
                          title={`Quran Progress: ${teacher.quranShiftsRecorded} out of ${teacher.totalExpectedShifts} expected shifts`}
                          arrow
                          placement="top"
                        >
                          <Chip
                            label={`Q: ${teacher.quranProgressPercentage}%`}
                            color={getPercentageColor(teacher.quranProgressPercentage)}
                            size="small"
                            variant="filled"
                            sx={{ fontSize: '0.6rem', height: '18px' }}
                          />
                        </Tooltip>
                        <Tooltip
                          title={`Greenbook Progress: ${teacher.greenbookShiftsRecorded} out of ${teacher.totalExpectedShifts} expected shifts`}
                          arrow
                          placement="top"
                        >
                          <Chip
                            label={`G: ${teacher.greenbookProgressPercentage}%`}
                            color={getPercentageColor(teacher.greenbookProgressPercentage)}
                            size="small"
                            variant="filled"
                            sx={{ fontSize: '0.6rem', height: '18px' }}
                          />
                        </Tooltip>
                      </Box>
                    )}
                  </TableCell>
                  <TableCell align="center" sx={{ 
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    display: { xs: 'none', lg: 'table-cell' },
                  }}>
                    {teacher.totalExpectedShifts > 0 ? (
                      <Tooltip
                        title={`Quran Progress: ${teacher.quranShiftsRecorded} out of ${teacher.totalExpectedShifts} expected shifts`}
                        arrow
                        placement="top"
                      >
                        <Chip
                          label={`${teacher.quranProgressPercentage}%`}
                          color={getPercentageColor(teacher.quranProgressPercentage)}
                          size="small"
                          variant="filled"
                          sx={{ 
                            fontSize: { xs: '0.65rem', sm: '0.75rem' },
                            height: { xs: '20px', sm: '24px' },
                          }}
                        />
                      </Tooltip>
                    ) : (
                      <Chip 
                        label="N/A" 
                        color="default" 
                        size="small" 
                        variant="outlined"
                        sx={{ 
                          fontSize: { xs: '0.65rem', sm: '0.75rem' },
                          height: { xs: '20px', sm: '24px' },
                        }}
                      />
                    )}
                  </TableCell>
                  <TableCell align="center" sx={{ 
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    display: { xs: 'none', lg: 'table-cell' },
                  }}>
                    {teacher.totalExpectedShifts > 0 ? (
                      <Tooltip
                        title={`Greenbook Progress: ${teacher.greenbookShiftsRecorded} out of ${teacher.totalExpectedShifts} expected shifts`}
                        arrow
                        placement="top"
                      >
                        <Chip
                          label={`${teacher.greenbookProgressPercentage}%`}
                          color={getPercentageColor(teacher.greenbookProgressPercentage)}
                          size="small"
                          variant="filled"
                          sx={{ 
                            fontSize: { xs: '0.65rem', sm: '0.75rem' },
                            height: { xs: '20px', sm: '24px' },
                          }}
                        />
                      </Tooltip>
                    ) : (
                      <Chip 
                        label="N/A" 
                        color="default" 
                        size="small" 
                        variant="outlined"
                        sx={{ 
                          fontSize: { xs: '0.65rem', sm: '0.75rem' },
                          height: { xs: '20px', sm: '24px' },
                        }}
                      />
                    )}
                  </TableCell>
                  <TableCell align="center" sx={{ 
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    display: { xs: 'none', md: 'table-cell' },
                  }}>
                    {teacher.hasSubstituteActivity ? (
                      <Tooltip
                        title={`Substitute assignments: ${teacher.substituteShifts}`}
                        arrow
                        placement="top"
                      >
                        <Chip
                          label={teacher.substituteShifts}
                          color="info"
                          size="small"
                          variant="filled"
                          sx={{ 
                            fontSize: { xs: '0.65rem', sm: '0.75rem' },
                            height: { xs: '20px', sm: '24px' },
                          }}
                        />
                      </Tooltip>
                    ) : (
                      <Chip 
                        label="0" 
                        color="default" 
                        size="small" 
                        variant="outlined"
                        sx={{ 
                          fontSize: { xs: '0.65rem', sm: '0.75rem' },
                          height: { xs: '20px', sm: '24px' },
                        }}
                      />
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {sortedTeachers.length === 0 && (
        <Box sx={{ textAlign: 'center', py: { xs: 2, sm: 3 } }}>
          <Typography 
            color="textSecondary"
            sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
          >
            No teachers found
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default TeacherActivityTracker;
